import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/apiClient';

export type CommsOrderStatus =
  | 'new'
  | 'in_progress'
  | 'awaiting_manual'
  | 'ineligible'
  | 'confirmed'
  | 'cancelled_by_customer'
  | 'unreachable'
  | 'manual_override'
  | 'halted';

export interface CommsFlowSummary {
  order_tracked_id: number;
  state: string;
  next_action_at: number | null;
  terminal: number;
}

export interface CommsOrder {
  id: number;
  shopify_order_id: string;
  order_name: string | null;
  phone_e164: string | null;
  customer_name: string | null;
  payment_gateways: string[] | null;
  financial_status: string | null;
  fulfillment_status: string | null;
  is_cod: number;
  total_price: string | null;
  currency: string | null;
  status: CommsOrderStatus;
  hold_applied: number;
  duplicate_of_order_id: number | null;
  shopify_created_at: number | null;
  created_at: number;
  updated_at: number;
  flow: CommsFlowSummary | null;
}

export interface CommsOrdersResponse {
  items: CommsOrder[];
  total: number;
  limit: number;
  offset: number;
  statuses: CommsOrderStatus[];
}

export interface CommsEvent {
  id: number;
  kind: string;
  channel: string | null;
  actor: string | null;
  detail: Record<string, unknown> | null;
  created_at: number;
}

export interface CommsJob {
  id: number;
  channel: string;
  purpose: string;
  status: string;
  attempts: number;
  max_attempts: number;
  run_at: number;
  sent_at: number | null;
  last_error: string | null;
  provider_ref: string | null;
}

export interface CommsOrderDetail {
  order: CommsOrder & {
    email: string | null;
    shipping_address: Record<string, string | null> | null;
    line_items: { title: string | null; variant_title: string | null; quantity: number | null }[] | null;
    shopify_tags: string | null;
    shopify_order_gid: string;
  };
  flow_runs: { id: number; state: string; next_action_at: number | null; terminal: number; attempt_count: number }[];
  events: CommsEvent[];
  risk_flags: { id: number; flag_type: string; resolved: number; created_at: number }[];
  jobs: CommsJob[];
}

export interface CommsHealth {
  ok: boolean;
  version: string;
  orders_by_status: Partial<Record<CommsOrderStatus, number>>;
  jobs_pending: number;
  jobs_failed: number;
  providers: Record<string, boolean>;
}

export type CommsSettings = Record<string, unknown> & {
  confirmation_enabled: boolean;
  apply_fulfillment_hold: boolean;
  auto_cancel_on_customer_cancel: boolean;
  auto_cancel_notify_customer: boolean;
  skip_test_orders: boolean;
  wa_wait_hours: number;
  call_attempts: number;
  call_gap_hours: number;
  sms_wait_hours: number;
  email_wait_hours: number;
  channels_enabled: Record<'whatsapp' | 'voice' | 'sms' | 'email', boolean>;
};

export type CommsAction = 'confirm' | 'cancel' | 'mark_unreachable' | 'restart' | 'start' | 'snooze' | 'skip_step';

const ORDERS_KEY = ['comms', 'orders'] as const;
const HEALTH_KEY = ['comms', 'health'] as const;
const SETTINGS_KEY = ['comms', 'settings'] as const;

export function useCommsHealth() {
  return useQuery({
    queryKey: HEALTH_KEY,
    queryFn: () => apiRequest<CommsHealth>('/api/comms/health'),
    refetchInterval: 30_000,
    retry: false,
  });
}

export function useCommsOrders(params: { status?: string; q?: string; limit?: number }) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  qs.set('limit', String(params.limit ?? 50));
  return useQuery({
    queryKey: [...ORDERS_KEY, params.status ?? '', params.q ?? '', params.limit ?? 50],
    queryFn: () => apiRequest<CommsOrdersResponse>(`/api/comms/orders?${qs.toString()}`),
    refetchInterval: 20_000,
  });
}

export function useCommsOrder(id: number | null) {
  return useQuery({
    queryKey: [...ORDERS_KEY, 'detail', id],
    queryFn: () => apiRequest<CommsOrderDetail>(`/api/comms/orders/${id}`),
    enabled: id !== null,
  });
}

export function useCommsOrderAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; action: CommsAction; hours?: number; note?: string }) =>
      apiRequest<{ ok: boolean; state?: string }>(`/api/comms/orders/${input.id}/action`, {
        method: 'POST',
        body: { action: input.action, hours: input.hours, note: input.note, actor: 'dashboard' },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORDERS_KEY });
      void qc.invalidateQueries({ queryKey: HEALTH_KEY });
    },
  });
}

export function useCommsSettings() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiRequest<{ settings: CommsSettings; defaults: CommsSettings }>('/api/comms/settings'),
  });
}

export function useUpdateCommsSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: Partial<CommsSettings>) =>
      apiRequest<{ settings: CommsSettings; errors: Record<string, string> }>('/api/comms/settings', {
        method: 'PUT',
        body: { settings },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}
